import React, { useContext, useEffect, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import { getOrgLevels, updateTemplateLevels } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import { GlobalTemplateContext } from '../../contexts/GlobalTemplateContext';

const TemplateLevelModal = ({ setSelectedAction, selectedTemplateId, levels }) => {
    const { userData } = useUserContext();
    const { getTemplatesRawData } = useContext(GlobalTemplateContext);
    const [availableLevels, setAvailableLevels] = useState([]);
    const [selectedLevels, setSelectedLevels] = useState(levels || []);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchOrgLevels = async () => {
            try {
                const orgLevels = await getOrgLevels(userData?.token, userData?.selectedOrganization?.org_token);
                setAvailableLevels(orgLevels.data || []);
            } catch (err) {
                console.error("Error fetching organization levels:", err);
            }
        };
        fetchOrgLevels();
    }, [userData]);

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const handleLevelChange = (levelId) => {
        setSelectedLevels((prevLevels) =>
            prevLevels.includes(levelId)
                ? prevLevels.filter((l) => l !== levelId)
                : [...prevLevels, levelId]
        );
    };

    const saveHandler = async () => {
        try {
            setSaving(true);
            setError('');
            const payload = {
                levels: selectedLevels
            };
            const res = await updateTemplateLevels(userData?.token, selectedTemplateId, payload);
            if (res.status) {
                getTemplatesRawData();
            }
            setSelectedAction(null);
        } catch (err) {
            setError(err?.message || "Error saving selected levels.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={true}
            onClose={() => setSelectedAction(null)}
            size="md"
        >
            <Modal.Header
                title="Edit Access Levels"
                subtitle="Select which organization levels can access this global template"
            />
            <Modal.Body className="space-y-4">
                <div>
                    <label className="stride-form-label">Available Levels</label>
                    <div className="relative">
                        <button
                            type="button"
                            onClick={toggleDropdown}
                            className="stride-input-field flex items-center justify-between cursor-pointer"
                        >
                            <span className={selectedLevels.length > 0 ? "text-stride-heading font-medium" : "text-stride-muted"}>
                                {selectedLevels.length > 0
                                    ? `${selectedLevels.length} level${selectedLevels.length === 1 ? '' : 's'} selected (${selectedLevels.join(', ')})`
                                    : "Select organization levels..."}
                            </span>
                            <ChevronDown size={16} className={`text-stride-muted transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {dropdownOpen && (
                            <div className="absolute left-0 right-0 mt-2 bg-white border border-border rounded-xl shadow-xl max-h-60 overflow-y-auto w-full z-50 p-2 space-y-1">
                                {availableLevels.length > 0 ? (
                                    availableLevels.map((level) => {
                                        const isSelected = selectedLevels.includes(level.id);
                                        return (
                                            <div
                                                key={level._id || level.id}
                                                onClick={() => handleLevelChange(level.id)}
                                                className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors text-xs md:text-sm ${
                                                    isSelected ? 'bg-primary-soft text-primary font-medium' : 'text-stride-body hover:bg-surface'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => {}} // handled by row click
                                                        className="rounded border-border-subtle text-primary focus:ring-primary/20 cursor-pointer"
                                                    />
                                                    <span>{level.id}</span>
                                                </div>
                                                {isSelected && <Check size={14} className="text-primary stroke-[2.5]" />}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="p-3 text-xs text-stride-muted text-center">No levels found</div>
                                )}
                            </div>
                        )}
                    </div>
                    <p className="stride-form-helper">
                        Organizations matching selected levels will have read and write permissions for this template.
                    </p>
                </div>

                {error && (
                    <div className="p-3 rounded-lg bg-danger-soft text-danger text-xs font-medium border border-danger/20">
                        {error}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline"
                    onClick={() => setSelectedAction(null)}
                    disabled={saving}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={saveHandler}
                    loading={saving}
                >
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TemplateLevelModal;
